import React, { useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  ChevronLeft,
  ChevronRight,
  Book,
  Tv,
  MessageSquare,
  Video,
  FileText,
  Handshake,
} from "lucide-react";
import { motion } from "framer-motion";

interface SliderProps {}

interface SliderSettings {
  infinite: boolean;
  speed: number;
  autoplay: boolean;
  autoplaySpeed: number;
  slidesToScroll: number;
  dots: boolean;
  arrows: boolean;
  nextArrow: JSX.Element;
  prevArrow: JSX.Element;
  beforeChange: (current: number, next: number) => void;
  responsive: {
    breakpoint: number;
    settings: {
      slidesToShow: number;
    };
  }[];
}

interface InfoCard {
  icon: React.ReactNode;
  title: string;
  heading: string;
  description: string;
  additionalText?: string;
}

const Approach: React.FC<SliderProps> = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Custom arrow components
  const NextArrow = (props: any) => {
    const { onClick } = props;
    return (
      <button
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg text-indigo-600 hover:bg-indigo-50 transition-all duration-300 transform hover:scale-110"
        onClick={onClick}>
        <ChevronRight size={20} />
      </button>
    );
  };

  const PrevArrow = (props: any) => {
    const { onClick } = props;
    return (
      <button
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg text-indigo-600 hover:bg-indigo-50 transition-all duration-300 transform hover:scale-110"
        onClick={onClick}>
        <ChevronLeft size={20} />
      </button>
    );
  };

  const settings: SliderSettings = {
    infinite: true,
    speed: 500,
    slidesToScroll: 1,
    dots: true,
    arrows: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    autoplay: true,
    autoplaySpeed: 5000,
    beforeChange: (current, next) => setCurrentIndex(next),
    responsive: [
      {
        breakpoint: 640, // for small screens
        settings: {
          slidesToShow: 1,
        },
      },
      {
        breakpoint: 1024, // for medium screens
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 9999, // for large screens
        settings: {
          slidesToShow: 3,
        },
      },
    ],
  };

  const infoCards: InfoCard[] = [
    {
      icon: <Book className="w-6 h-6" />,
      title: "Lesson Notes",
      heading: "Comprehensive Lesson",
      description:
        "Organize and share your lesson materials, including documents, presentations, and multimedia resources, with your students for a seamless learning experience",
    },
    {
      icon: <Tv className="w-6 h-6" />,
      title: "Interactive Whiteboard",
      heading: "Leverage our powerful Whiteboard",
      description:
        "Enhance your teaching with our interactive whiteboard, enabling you to annotate, draw, and share content, creating a dynamic and immersive learning environment for your students",
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "Q&A Sessions",
      heading: "Facilitate engaging Q&A sessions",
      description:
        "Encourage active participation and foster meaningful discussions through our seamless educational platform, where you can respond to your students' inquiries and provide immediate feedback",
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "Video Conferencing",
      heading: "High-Quality Interactions",
      description:
        "Deliver live, face-to-face lessons through our robust video conferencing capabilities, enabling you to create an engaging and interactive learning environment for your students",
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Assignments",
      heading: "Track Student Progress",
      description:
        "Assign and manage homework, quizzes, and projects with ease, and monitor your students' progress to ensure they are meeting their learning objectives",
    },
    {
      icon: <Handshake className="w-6 h-6" />,
      title: "Partnerships",
      heading: "Trusted by Leading Institutions",
      description:
        "Join hands with EduConnect and leverage our extensive network of partnerships to elevate your teaching brand and reach a wider audience",
      additionalText: "Explore Partnerships",
    },
  ];

  return (
    <section className="relative overflow-hidden py-20 bg-gradient-to-br from-indigo-50 to-indigo-100">
      {/* Decorative elements */}
      <div
         className="absolute top-0 left-0 w-64 h-64 bg-indigo-200 rounded-full opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
      <div
         className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-300 rounded-full opacity-20 translate-x-1/3 translate-y-1/3"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center justify-center mb-16">
          <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }} className="inline-block px-4 py-2 rounded-full bg-indigo-100 text-indigo-700 font-medium text-sm mb-4">
            Why Choose Us
          </motion.span>
          <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }} className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Our Approach
          </motion.h2>
          <div className="w-24 h-1 bg-indigo-600 rounded-full"></div>
          <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }} className="mt-6 text-lg text-gray-600 text-center max-w-2xl">
            Discover how our platform transforms online education with tools
            designed for interactive and effective learning experiences.
          </motion.p>
        </div>

        <div className="relative px-8">
          <Slider {...settings} className="w-full">
            {infoCards.map((card, index) => (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 }}
                key={index}
                className="px-4 py-2"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}>
                <article
                  className={`relative overflow-hidden flex flex-col h-96 p-8 rounded-2xl border border-gray-100 
                    ${
                      hoveredIndex === index
                        ? "bg-indigo-600 text-white shadow-xl"
                        : "bg-white text-gray-800 shadow-md"
                    }
                    transition-all duration-500 ease-in-out transform hover:-translate-y-2`}>
                  {/* Top design element */}
                  <div className="absolute top-0 right-0 w-32 h-32 -mt-8 -mr-8 bg-indigo-100 rounded-full opacity-20"></div>

                  {/* Icon container */}
                  <div
                    className={`flex items-center justify-center w-16 h-16 rounded-2xl mb-6 
                    ${
                      hoveredIndex === index
                        ? "bg-white text-indigo-600"
                        : "bg-indigo-100 text-indigo-600"
                    } 
                    transition-all duration-500`}>
                    {card.icon}
                  </div>

                  {/* Card title */}
                  <span
                    className={`text-sm font-semibold uppercase tracking-wider mb-4 
                    ${
                      hoveredIndex === index
                        ? "text-indigo-100"
                        : "text-indigo-500"
                    } 
                    transition-all duration-500`}>
                    {card.title}
                  </span>

                  {/* Card heading */}
                  <h3 className="text-xl font-bold mb-4">{card.heading}</h3>

                  {/* Card description */}
                  <p
                    className={`text-base leading-relaxed mb-6 
                    ${
                      hoveredIndex === index
                        ? "text-indigo-100"
                        : "text-gray-600"
                    } 
                    transition-all duration-500`}>
                    {card.description}
                  </p>

                  {/* Additional text or CTA */}
                  {card.additionalText && (
                    <div className="mt-auto">
                      <button
                        className={`mt-4 inline-flex items-center font-medium 
                        ${
                          hoveredIndex === index
                            ? "text-white"
                            : "text-indigo-600"
                        } 
                        transition-all duration-500`}>
                        {card.additionalText}
                        <ChevronRight className="ml-2 w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Bottom design element */}
                  <div className="absolute bottom-0 left-0 w-24 h-24 -ml-12 -mb-12 bg-indigo-200 rounded-full opacity-20"></div>
                </article>
              </motion.div>
            ))}
          </Slider>
        </div>

        {/* Indicator dots with customization */}
        <div className="flex justify-center mt-12">
          {infoCards.map((_, index) => (
            <button
              key={index}
              onClick={() => {}} // This would be connected to the slider in a full implementation
              className={`mx-1 w-3 h-3 rounded-full transition-all duration-300 
                ${
                  currentIndex === index ? "bg-indigo-600 w-8" : "bg-indigo-300"
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Approach;
